/**
 * Widget účtů. Neví, jestli člověk sedí v Entity, v Keycloaku, v Sambě nebo v AD.
 * Vždy volá stejný tvar: `{ action, source }` na nástroj, který zdroj uvedl.
 * Dá se položit na libovolný panel. Hlavičku kreslí WidgetShell.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  ConfirmDialog,
  PasswordField,
  type PasswordCheckResult,
  type PasswordContext,
  type PasswordReasonView,
  EmptyState,
  EntityAvatar,
  FilterChips,
  Input,
  Label,
  Modal,
  SearchInput,
  Select,
  Textarea,
  Toggle,
} from '../components/ui';
import { MAX_IMPORT, parseAccountTable, type ImportedAccount } from '../lib/account-import';
import { PEOPLE_CALL_DEADLINE_MS, sourceChipLabel, withDeadline } from '../lib/people-sources';
import {
  makePassword,
  type PasswordComplexity,
  type PasswordMode,
} from '../lib/user-password';
import { PASSWORD_FALLBACK_MIN_LENGTH, passwordHints } from '../lib/password-hints';
import { callWidgetTool } from '../widgets/scope';
import type { WidgetRenderProps } from '../widgets/types';
import {
  Building2,
  Check,
  ChevronDown,
  Copy,
  KeyRound,
  LockOpen,
  Monitor,
  Printer,
  RefreshCw,
  Server,
  Shield,
  UserPlus,
  Users,
  X,
} from 'lucide-react';

const STORAGE_KEY = 'people-active-source';

type Kind = 'entity' | 'keycloak' | 'samba' | 'ad';

interface Source {
  id: string;
  owner: string;
  tool: string;
  kind: Kind;
  label: string;
  detail: string;
  available: boolean;
  can_write: boolean;
  error?: string;
  /** personal = zápis jen pod osobním master účtem s kontraktem (AD). */
  write_mode?: 'personal';
  master_login?: string | null;
  master_reason?: string;
}

type PasswordState = 'valid' | 'expired' | 'must_change' | 'none' | 'unknown';

/** Rychlé filtry seznamu lidí. Počítají se nad načteným výpisem. */
type Quick = 'all' | 'locked' | 'must_change' | 'expired' | 'expiring' | 'disabled' | 'inactive';

interface Place {
  id: string;
  name: string;
  path: string;
}

const DAY = 86_400_000;

function matchesQuick(user: Person, quick: Quick, kind: Kind | undefined, now: number): boolean {
  if (quick === 'locked') return user.locked === true;
  if (quick === 'must_change') return user.password_state === 'must_change';
  if (quick === 'expired') return user.password_state === 'expired';
  if (quick === 'expiring') {
    if (user.password_state !== 'valid' || !user.password_expires_at) return false;
    const end = Date.parse(user.password_expires_at);
    return Number.isFinite(end) && end - now <= 14 * DAY;
  }
  if (quick === 'disabled') return user.enabled === false;
  if (quick === 'inactive') {
    if (!user.last_logon_at) return kind === 'ad' || kind === 'samba';
    const last = Date.parse(user.last_logon_at);
    return Number.isFinite(last) && now - last > 90 * DAY;
  }
  return true;
}

interface Person {
  id: string;
  login: string;
  display_name: string;
  email: string;
  enabled: boolean | null;
  place: string;
  origin: string;
  groups?: string[];
  password_state?: PasswordState;
  password_expires_at?: string | null;
  password_never_expires?: boolean | null;
  account_expires_at?: string | null;
  last_logon_at?: string | null;
  locked?: boolean | null;
  upn?: string;
  description?: string;
  home_directory?: string;
  home_drive?: string;
  profile_path?: string;
  logon_script?: string;
  phone?: string;
  mobile?: string;
  department?: string;
  job_title?: string;
  office?: string;
  created_at?: string | null;
}

function formatDay(iso?: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString();
}

function formatWhen(iso?: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
}

function groupPreview(groups?: string[]): string {
  const list = groups ?? [];
  if (list.length === 0) return '';
  const head = list.slice(0, 2).join(', ');
  return list.length > 2 ? `${head} +${list.length - 2}` : head;
}

const EDIT_KEYS = [
  'display_name',
  'email',
  'description',
  'upn',
  'home_directory',
  'home_drive',
  'profile_path',
  'logon_script',
  'phone',
  'mobile',
  'department',
  'job_title',
  'office',
] as const;
type EditKey = typeof EDIT_KEYS[number];
type Draft = Record<EditKey, string>;

function draftFrom(user: Person): Draft {
  return {
    display_name: user.display_name ?? '',
    email: user.email ?? '',
    description: user.description ?? '',
    upn: user.upn ?? '',
    home_directory: user.home_directory ?? '',
    home_drive: user.home_drive ?? '',
    profile_path: user.profile_path ?? '',
    logon_script: user.logon_script ?? '',
    phone: user.phone ?? '',
    mobile: user.mobile ?? '',
    department: user.department ?? '',
    job_title: user.job_title ?? '',
    office: user.office ?? '',
  };
}

function profileDelta(user: Person, draft: Draft): Record<string, string> {
  const before = draftFrom(user);
  const out: Record<string, string> = {};
  for (const key of EDIT_KEYS) {
    if (draft[key] !== before[key]) out[key] = draft[key];
  }
  return out;
}

function ProfileField({
  label,
  value,
  editing,
  onChange,
  className,
}: {
  label: string;
  value: string;
  editing: boolean;
  onChange?: (value: string) => void;
  className?: string;
}) {
  return (
    <label className={className}>
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
      {editing && onChange
        ? <Input className="mt-1" value={value} onChange={(event) => onChange(event.target.value)} />
        : <p className={`mt-1 break-all text-sm ${value.trim() ? '' : 'text-muted-foreground'}`}>{value.trim() || '—'}</p>}
    </label>
  );
}

function ProfileSection({
  title,
  children,
  extra,
  className,
}: {
  title: string;
  children: ReactNode;
  extra?: ReactNode;
  className?: string;
}) {
  return (
    <section className={`rounded-xl border border-white/10 bg-white/[0.02] p-4 ${className ?? ''}`}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold tracking-tight">{title}</h3>
        {extra}
      </div>
      {children}
    </section>
  );
}

function downloadTable(
  rows: Array<{ login: string; name: string; password: string }>,
  headers: [string, string, string],
) {
  const esc = (value: string) => `"${value.replace(/"/g, '""')}"`;
  const lines = [headers, ...rows.map((row) => [row.login, row.name, row.password])]
    .map((line) => line.map(esc).join(';'))
    .join('\r\n');
  const blob = new Blob(['\uFEFF' + lines], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'accounts.csv';
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const IMPORT_TEMPLATE = 'login;jmeno;prijmeni;email;heslo;skupina\r\n';

function csvCell(value: string): string {
  if (/[;"\n\r]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

function downloadText(filename: string, text: string) {
  const blob = new Blob(['\uFEFF' + text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="rounded-lg bg-black/20 px-3 py-2">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="mt-1 text-sm">{children}</div>
    </div>
  );
}

type Field = 'all' | 'name' | 'login' | 'email' | 'place' | 'department' | 'group';
type Pane = 'people' | 'groups' | 'stations';

interface Station {
  id: string;
  name: string;
  dns: string;
  os: string;
  enabled: boolean | null;
  place: string;
  origin: string;
  description: string;
  last_logon_at?: string | null;
  kind: string;
}

interface FleetAgent {
  agent_id?: string;
  hostname?: string;
  os?: string;
  os_version?: string;
  status?: string;
}

interface ConsoleSession {
  computer: string;
  user: string;
  since: string;
  source: 'station' | 'dc';
}

interface DeskStation {
  hostname?: string;
  rustdesk_id?: string | null;
}

function DeskLink({ id }: { id?: string | null }) {
  const { t } = useTranslation();
  if (!id) return <span className="text-muted-foreground">—</span>;
  return (
    <Button
      size="sm"
      variant="secondary"
      onClick={(event) => {
        event.stopPropagation();
        globalThis.open(`rustdesk://${id}`, '_blank');
      }}
    >{t('people.rustdeskOpen', 'Připojit plochu')}</Button>
  );
}

function SignedIn({ sessions }: { sessions: ConsoleSession[] }) {
  const { t } = useTranslation();
  if (!sessions.length) return <span className="text-muted-foreground">—</span>;
  const first = sessions[0]!;
  const extra = sessions.length > 1 ? ` +${sessions.length - 1}` : '';
  const from = first.source === 'dc'
    ? t('people.sessionFromDc', 'Z řadiče')
    : t('people.sessionFromStation', 'Ze stanice');
  return (
    <span className="block min-w-0">
      <span className="block truncate">{first.user}{extra}</span>
      <span className="block truncate text-xs text-muted-foreground">{formatWhen(first.since)} · {from}</span>
    </span>
  );
}

function hostKey(value: string): string {
  return value.trim().toLowerCase().replace(/\$$/, '').split('.')[0] ?? '';
}

interface DirGroup {
  id: string;
  name: string;
  description: string;
  members: number | null;
  kind: string;
}

interface ToolOk {
  ok?: boolean;
  error?: string;
  sources?: Source[];
  users?: Person[];
  user?: Person;
  groups?: DirGroup[];
  stations?: Station[];
  places?: Place[];
  password?: string;
  truncated?: boolean;
}

const OWNERS: Array<{ tool: string; kind: Kind }> = [
  { tool: 'identity.users', kind: 'entity' },
  { tool: 'sso.users', kind: 'keycloak' },
  { tool: 'ad.users', kind: 'ad' },
];

function sourceKey(source: Pick<Source, 'tool' | 'id'>): string {
  return `${source.tool}::${source.id}`;
}

/**
 * Server heslo odmítl (password_rejected) nebo ho nešlo ověřit proti únikům
 * (password_unverified) — nese důvody, ne obecnou chybu.
 */
class PasswordRejected extends Error {
  constructor(
    readonly reasons: PasswordReasonView[],
    message: string,
    readonly code: 'password_rejected' | 'password_unverified',
    /** U password_unverified: jde pokračovat se souhlasem (jen založení účtu). */
    readonly consentPossible: boolean,
  ) {
    super(message);
  }
}

function unwrap(result: ToolOk): ToolOk {
  if (result?.ok === false) {
    const rejected = result as ToolOk & {
      code?: string;
      reasons?: PasswordReasonView[];
      consent_possible?: boolean;
    };
    if (
      (rejected.code === 'password_rejected' || rejected.code === 'password_unverified') &&
      Array.isArray(rejected.reasons)
    ) {
      throw new PasswordRejected(
        rejected.reasons,
        rejected.reasons.map((reason) => reason.message_cs).join(' ') || rejected.error || '',
        rejected.code,
        rejected.consent_possible === true,
      );
    }
    throw new Error(result.error || 'Operace selhala');
  }
  return result;
}

/** Které řádky hromadné akce neprošly a proč (odmítnuté heslo i jiná chyba). */
function RowErrors({ rows }: { rows: Array<{ login: string; message: string }> }) {
  const { t } = useTranslation();
  if (rows.length === 0) return null;
  return (
    <Alert variant="destructive">
      <p className="text-sm font-medium">{t('people.rowsFailed', { count: rows.length })}</p>
      <ul className="mt-1 max-h-40 space-y-0.5 overflow-auto text-sm">
        {rows.map((row) => (
          <li key={row.login}><span className="font-mono">{row.login}</span>: {row.message}</li>
        ))}
      </ul>
    </Alert>
  );
}

function KindIcon({ kind, className }: { kind: Kind; className?: string }) {
  if (kind === 'entity') return <Shield className={className} />;
  if (kind === 'keycloak') return <KeyRound className={className} />;
  if (kind === 'samba') return <Server className={className} />;
  return <Building2 className={className} />;
}

/** Výchozí režim hesla: žákům snadné ze slov, ostatním silné (Filip 2026-10-22). */
function defaultPasswordMode(people: Person[]): PasswordMode {
  const pupil = (person: Person) => /t[rř][ií]da|[zž][aá]ci/i.test(person.place ?? '');
  return people.length > 0 && people.every(pupil) ? 'easy' : 'strong';
}

function PasswordChooser({
  mode,
  onMode,
  value,
  onValue,
  words,
  onWords,
  complexity,
  onComplexity,
  context,
  minLength,
  minLengthSource,
  fieldMode,
  check,
  showUnique = false,
  uniqueEach = false,
  onUniqueEach,
  showTemporary = false,
  temporary = false,
  onTemporary,
  rejected,
}: {
  mode: PasswordMode;
  onMode: (value: PasswordMode) => void;
  value: string;
  onValue: (value: string) => void;
  words: 2 | 3;
  onWords: (value: 2 | 3) => void;
  complexity: PasswordComplexity;
  onComplexity: (value: PasswordComplexity) => void;
  /** Kontext účtu pro kontrolu (login, jména, maily); u hromadné akce prázdný. */
  context?: PasswordContext;
  /** Minimum podle politiky adresáře (action password_policy). */
  minLength?: number;
  minLengthSource?: string | null;
  /** create = nové účty (neověřené heslo jde se souhlasem), change = změna hesla. */
  fieldMode: 'create' | 'change';
  check: (args: Record<string, unknown>) => Promise<PasswordCheckResult>;
  showUnique?: boolean;
  uniqueEach?: boolean;
  onUniqueEach?: (value: boolean) => void;
  showTemporary?: boolean;
  temporary?: boolean;
  onTemporary?: (value: boolean) => void;
  /** Důvody, proč server heslo při uložení odmítl. */
  rejected?: PasswordReasonView[] | null;
}) {
  const { t } = useTranslation();
  const [advanced, setAdvanced] = useState(false);
  const patch = (next: Partial<PasswordComplexity>) => onComplexity({ ...complexity, ...next });
  const field = {
    value,
    context,
    minLength,
    minLengthSource,
    mode: fieldMode,
    check,
    serverReasons: rejected,
    requireComplexity: true,
  };
  const regenerate = () => onValue(makePassword(mode, { words, complexity }));
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t('people.copied'));
    } catch {
      toast.error(t('people.copyFailed', 'Zkopírovat se nepovedlo'));
    }
  };
  return (
    <div className="space-y-4">
      <FilterChips
        label={t('people.passwordKind', 'Druh hesla')}
        value={mode}
        onChange={(next) => {
          onMode(next);
          onValue(next === 'manual' ? '' : makePassword(next, { words, complexity }));
        }}
        options={[
          { value: 'easy', label: t('people.modeEasy', 'Snadno zapamatovatelné') },
          { value: 'strong', label: t('people.modeStrong', 'Silné') },
          { value: 'manual', label: t('people.modeManual', 'Napíšu sám') },
        ]}
      />

      {mode !== 'manual' && (
        <div className="space-y-2">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <code className="flex-1 select-all rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 text-center font-mono text-xl tracking-wide">
              {value}
            </code>
            <div className="flex gap-2">
              <Button variant="secondary" size="md" className="h-10" icon={RefreshCw} onClick={regenerate}>{t('people.another', 'Jiné')}</Button>
              <Button variant="secondary" size="md" className="h-10" icon={Copy} onClick={() => void copy()}>{t('people.copy')}</Button>
            </div>
          </div>
          {mode === 'easy' && (
            <FilterChips
              label={t('people.wordCount', 'Počet slov')}
              value={String(words) as '2' | '3'}
              onChange={(next) => {
                const count = next === '2' ? 2 : 3;
                onWords(count);
                onValue(makePassword('easy', { words: count }));
              }}
              options={[
                { value: '2', label: t('people.words2', '2 slova') },
                { value: '3', label: t('people.words3', '3 slova (bezpečnější)') },
              ]}
            />
          )}
          {mode === 'strong' && (
            <div className="space-y-3">
              <Button variant="ghost" icon={ChevronDown} onClick={() => setAdvanced(!advanced)}>
                {t('people.advanced', 'Upravit složení')}
              </Button>
              {advanced && (
                <div className="space-y-3 rounded-lg border border-white/10 p-3">
                  <div className="grid gap-3 sm:grid-cols-[8rem_1fr]">
                    <div>
                      <Label>{t('people.length', 'Délka')}</Label>
                      <Input
                        type="number"
                        min={Math.max(8, minLength ?? PASSWORD_FALLBACK_MIN_LENGTH)}
                        max={64}
                        value={complexity.length ?? 16}
                        onChange={(event) => {
                          const next = Number(event.target.value);
                          patch({ length: Number.isFinite(next) ? next : 16 });
                        }}
                      />
                    </div>
                    <div className="flex flex-wrap items-end gap-x-4 gap-y-2 pb-1 text-sm">
                      {([
                        ['upper', t('people.upper', 'Velká')],
                        ['lower', t('people.lower', 'Malá')],
                        ['digits', t('people.digits', 'Číslice')],
                        ['special', t('people.symbols', 'Znaky')],
                      ] as const).map(([key, label]) => (
                        <label key={key} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={complexity[key] !== false}
                            onChange={(event) => patch({ [key]: event.target.checked })}
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={complexity.skipAmbiguous !== false}
                      onChange={(event) => patch({ skipAmbiguous: event.target.checked })}
                    />
                    {t('people.skipAmbiguous', 'Bez 0, O, 1, l a I')}
                  </label>
                  <Button variant="secondary" size="md" className="h-10" icon={RefreshCw} onClick={regenerate}>{t('people.generate', 'Vygenerovat')}</Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {mode === 'manual' && (
        <PasswordField {...field} label={t('people.typePassword', 'Nové heslo')} onChange={onValue} />
      )}
      {mode !== 'manual' && <PasswordField {...field} readOnly />}

      {showUnique && mode !== 'manual' && (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={uniqueEach}
            onChange={(event) => onUniqueEach?.(event.target.checked)}
          />
          {t('people.uniqueEach', 'Každý účet dostane vlastní')}
        </label>
      )}
      {showUnique && uniqueEach && mode !== 'manual' && (
        <p className="text-xs text-muted-foreground">{t('people.uniqueNote', 'Vyplněné pole je jen ukázka. Uložení dá každému účtu nové.')}</p>
      )}
      {showTemporary && (
        <div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={temporary}
              onChange={(event) => onTemporary?.(event.target.checked)}
            />
            {t('people.mustChange', 'Při přihlášení si ho musí změnit')}
          </label>
          {temporary && (
            <p className="mt-1 pl-6 text-xs text-muted-foreground">
              {t('people.mustChangeHint', 'Při prvním přihlášení si zvolí vlastní heslo, které mu pak už platí.')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** Lístečky s heslem k vytištění: jméno, login, heslo, poznámka o změně. */
function printSlips(
  rows: Array<{ login: string; name: string; password: string }>,
  labels: { title: string; login: string; password: string; note: string },
) {
  const view = window.open('', '_blank', 'width=800,height=900');
  if (!view) return false;
  const doc = view.document;
  doc.title = labels.title;
  const style = doc.createElement('style');
  style.textContent = [
    'body{font-family:Arial,sans-serif;margin:16px;color:#000}',
    '.slip{border:1px dashed #555;padding:12px 16px;margin:0 0 12px;break-inside:avoid}',
    '.name{font-size:18px;font-weight:bold}',
    '.row{margin-top:6px;font-size:14px}',
    '.pw{font-family:Consolas,monospace;font-size:22px;letter-spacing:1px}',
    '.note{margin-top:8px;font-size:12px;color:#333}',
  ].join('');
  doc.head.appendChild(style);
  for (const row of rows) {
    const slip = doc.createElement('div');
    slip.className = 'slip';
    const add = (className: string, text: string) => {
      const el = doc.createElement('div');
      el.className = className;
      el.textContent = text;
      slip.appendChild(el);
      return el;
    };
    add('name', row.name || row.login);
    add('row', `${labels.login}: ${row.login}`);
    const pw = add('row', `${labels.password}: `);
    const code = doc.createElement('span');
    code.className = 'pw';
    code.textContent = row.password;
    pw.appendChild(code);
    if (labels.note) add('note', labels.note);
    doc.body.appendChild(slip);
  }
  view.focus();
  view.print();
  return true;
}

export default function DirectoryAccounts({ scope }: WidgetRenderProps) {
  const { t } = useTranslation();
  const call = <T,>(tool: string, args: Record<string, unknown>) =>
    callWidgetTool<T>(tool, args, scope, { silent: true });
  const queryClient = useQueryClient();
  const [activeKey, setActiveKey] = useState(() =>
    typeof window === 'undefined' ? '' : localStorage.getItem(STORAGE_KEY) ?? ''
  );
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [opened, setOpened] = useState<Person | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [more, setMore] = useState(false);
  const [openedGroup, setOpenedGroup] = useState<DirGroup | null>(null);
  const [openedStation, setOpenedStation] = useState<Station | null>(null);
  const [groupDraft, setGroupDraft] = useState('');
  const [addQuery, setAddQuery] = useState('');
  const [addDebounced, setAddDebounced] = useState('');
  const [passwordFor, setPasswordFor] = useState<Person | null>(null);
  const [customPassword, setCustomPassword] = useState('');
  const [temporary, setTemporary] = useState(true);
  const [uniqueEach, setUniqueEach] = useState(true);
  const [complexity, setComplexity] = useState<PasswordComplexity>({
    length: 16,
    upper: true,
    lower: true,
    digits: true,
    special: true,
    skipAmbiguous: true,
  });
  const [passMode, setPassMode] = useState<PasswordMode>('strong');
  const [words, setWords] = useState<2 | 3>(3);
  const [passwordDone, setPasswordDone] = useState<Array<{ login: string; name: string; password: string }>>([]);
  const [issued, setIssued] = useState<Array<{ login: string; name: string; password: string }>>([]);
  const [creating, setCreating] = useState(false);
  const [createMode, setCreateMode] = useState<'one' | 'table'>('one');
  const [importText, setImportText] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState({ login: '', first: '', last: '', email: '' });
  const [secret, setSecret] = useState<{ login: string; password: string } | null>(null);
  const [rejected, setRejected] = useState<PasswordReasonView[] | null>(null);
  const [consent, setConsent] = useState<{ rows: ImportedAccount[]; mode: 'one' | 'table' } | null>(null);
  const [rowErrors, setRowErrors] = useState<Array<{ login: string; message: string }>>([]);
  const [picked, setPicked] = useState<string[]>([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignIds, setAssignIds] = useState<string[] | null>(null);
  const [groupName, setGroupName] = useState('');
  const [pane, setPane] = useState<Pane>('people');
  const [field, setField] = useState<Field>('all');
  const [status, setStatus] = useState<'' | 'on' | 'off'>('');
  const [quick, setQuick] = useState<Quick>('all');
  const [placeFilter, setPlaceFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => setRejected(null), [customPassword]);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const timer = setTimeout(() => setAddDebounced(addQuery.trim()), 250);
    return () => clearTimeout(timer);
  }, [addQuery]);

  const sourcesQ = useQuery({
    queryKey: ['people-sources', scope.node, scope.identity],
    queryFn: async () => {
      const batches = await Promise.all(OWNERS.map(async (owner) => {
        try {
          const result = await withDeadline(
            call<ToolOk>(owner.tool, { action: 'sources' }),
            PEOPLE_CALL_DEADLINE_MS,
            t('people.callTimeout', { seconds: PEOPLE_CALL_DEADLINE_MS / 1000 }),
          );
          if (result?.ok === false) {
            return [{
              id: owner.kind,
              owner: owner.kind,
              tool: owner.tool,
              kind: owner.kind,
              label: owner.kind,
              detail: '',
              available: false,
              can_write: false,
              error: result.error || t('people.unavailable'),
            } satisfies Source];
          }
          return result.sources ?? [];
        } catch (error) {
          return [{
            id: owner.kind,
            owner: owner.kind,
            tool: owner.tool,
            kind: owner.kind,
            label: owner.kind,
            detail: '',
            available: false,
            can_write: false,
            error: (error as Error).message,
          } satisfies Source];
        }
      }));
      return batches.flat();
    },
  });

  const sources = sourcesQ.data ?? [];
  const active = sources.find((source) => sourceKey(source) === activeKey) ?? null;

  useEffect(() => {
    if (sources.length === 0) return;
    const still = sources.some((source) => sourceKey(source) === activeKey && source.available);
    if (still) return;
    const next = sources.find((source) => source.available) ?? sources[0];
    if (!next) return;
    const key = sourceKey(next);
    setActiveKey(key);
    localStorage.setItem(STORAGE_KEY, key);
  }, [sources, activeKey]);

  const serverQuery = field === 'place' ? '' : debounced;
  const usersQ = useQuery({
    queryKey: ['people-users', scope.node, scope.identity, active ? sourceKey(active) : '', debounced, placeFilter, groupFilter],
    enabled: Boolean(active?.available) && pane === 'people',
    // Výpis, který selže nebo nedoběhne, se nezkouší znovu: tři pokusy po 30 s
    // vypadaly jako věčné „Načítám lidi…“ (ZSHACM001 2026-10-29). Chyba se ukáže hned.
    retry: false,
    // Rychlé filtry se počítají nad výpisem, proto bez enabled a s vyšším stropem.
    queryFn: () =>
      withDeadline(
        call<ToolOk>(active!.tool, {
          action: 'list',
          source: active!.id,
          query: debounced || undefined,
          field: 'all',
          ou: placeFilter || undefined,
          group: groupFilter || undefined,
          limit: 500,
        }),
        PEOPLE_CALL_DEADLINE_MS,
        t('people.callTimeout', { seconds: PEOPLE_CALL_DEADLINE_MS / 1000 }),
      ).then(unwrap),
  });

  const placesQ = useQuery({
    queryKey: ['people-places', scope.node, scope.identity, active ? sourceKey(active) : ''],
    enabled: Boolean(active?.available),
    queryFn: () =>
      call<ToolOk>(active!.tool, { action: 'places', source: active!.id })
        .then(unwrap)
        .catch(() => ({ ok: true, places: [] } as ToolOk)),
  });

  const groupsQ = useQuery({
    queryKey: ['people-groups', scope.node, scope.identity, active ? sourceKey(active) : ''],
    enabled: Boolean(active?.available),
    queryFn: () =>
      call<ToolOk>(active!.tool, {
        action: 'groups',
        source: active!.id,
        limit: 500,
      }).then(unwrap),
  });

  const stationsQ = useQuery({
    queryKey: ['people-stations', scope.node, scope.identity, active ? sourceKey(active) : '', serverQuery, field, status],
    enabled: Boolean(active?.available) && pane === 'stations',
    queryFn: () =>
      call<ToolOk>(active!.tool, {
        action: 'stations',
        source: active!.id,
        query: serverQuery || undefined,
        field: field === 'place' ? 'all' : field,
        enabled: status === 'on' ? true : status === 'off' ? false : undefined,
        limit: 200,
      }).then(unwrap),
  });

  const deskQ = useQuery({
    queryKey: ['people-rustdesk', scope.node, scope.identity],
    enabled: pane === 'stations',
    queryFn: async () => {
      try {
        const data = await call<{ ok?: boolean; stations?: DeskStation[] }>('rustdesk.list', {});
        if (data?.ok === false) return [] as DeskStation[];
        return data.stations ?? [];
      } catch {
        return [] as DeskStation[];
      }
    },
  });

  const sessionsQ = useQuery({
    queryKey: ['people-sessions', scope.node, scope.identity],
    enabled: pane === 'stations',
    queryFn: async () => {
      try {
        const data = await call<{ ok?: boolean; sessions?: ConsoleSession[] }>('fleet.sessions', {});
        if (data?.ok === false) return [] as ConsoleSession[];
        return data.sessions ?? [];
      } catch {
        return [] as ConsoleSession[];
      }
    },
  });

  const fleetQ = useQuery({
    queryKey: ['people-fleet', scope.node, scope.identity],
    enabled: pane === 'stations',
    queryFn: async () => {
      try {
        const data = await call<FleetAgent[] | { agents?: FleetAgent[]; ok?: boolean }>('fleet.agent_list', {});
        if (Array.isArray(data)) return data;
        return data.agents ?? [];
      } catch {
        return [] as FleetAgent[];
      }
    },
  });

  const membersQ = useQuery({
    queryKey: ['people-group-members', scope.node, scope.identity, active ? sourceKey(active) : '', openedGroup?.name ?? ''],
    enabled: Boolean(active?.available && openedGroup),
    queryFn: () =>
      call<ToolOk>(active!.tool, {
        action: 'list',
        source: active!.id,
        group: openedGroup!.name,
        limit: 200,
      }).then(unwrap),
  });

  const candidatesQ = useQuery({
    queryKey: ['people-candidates', scope.node, scope.identity, active ? sourceKey(active) : '', addDebounced],
    enabled: Boolean(active?.available && openedGroup && addDebounced.length > 0),
    queryFn: () =>
      call<ToolOk>(active!.tool, {
        action: 'list',
        source: active!.id,
        query: addDebounced,
        field: 'all',
        limit: 20,
      }).then(unwrap),
  });

  const loaded = usersQ.data?.users ?? [];
  const needle = debounced.trim().toLowerCase();
  const now = Date.now();
  const quickCount = (value: Quick) => loaded.filter((user) => matchesQuick(user, value, active?.kind, now)).length;
  const users = loaded.filter((user) => matchesQuick(user, quick, active?.kind, now));
  const places = placesQ.data?.places ?? [];
  const directoryGroups = groupsQ.data?.groups ?? [];
  const shownGroups = directoryGroups.filter((group) => {
    if (pane !== 'groups' || !needle) return true;
    return group.name.toLowerCase().includes(needle) || group.description.toLowerCase().includes(needle);
  });
  const fleetAgents = fleetQ.data ?? [];
  const sessionsByHost = new Map<string, ConsoleSession[]>();
  for (const session of sessionsQ.data ?? []) {
    const key = hostKey(session.computer);
    if (!key) continue;
    const list = sessionsByHost.get(key) ?? [];
    list.push(session);
    sessionsByHost.set(key, list);
  }
  const sessionsFor = (name: string, dns = ''): ConsoleSession[] =>
    sessionsByHost.get(hostKey(name)) ?? sessionsByHost.get(hostKey(dns)) ?? [];
  const deskByHost = new Map<string, string>();
  for (const station of deskQ.data ?? []) {
    const key = hostKey(station.hostname ?? '');
    if (key && station.rustdesk_id) deskByHost.set(key, station.rustdesk_id);
  }
  const deskFor = (name: string, dns = ''): string | null =>
    deskByHost.get(hostKey(name)) ?? deskByHost.get(hostKey(dns)) ?? null;
  const fleetByHost = new Map<string, FleetAgent>();
  for (const agent of fleetAgents) {
    const key = hostKey(agent.hostname ?? '');
    if (key) fleetByHost.set(key, agent);
  }
  const stationRows = (stationsQ.data?.stations ?? []).filter((station) => {
    if (field === 'place' && needle && !station.place.toLowerCase().includes(needle)) return false;
    return true;
  }).map((station) => {
    const agent = fleetByHost.get(hostKey(station.name)) ?? fleetByHost.get(hostKey(station.dns));
    const fleet = agent ? (agent.status === 'online' ? 'online' : 'offline') : 'none';
    return { ...station, fleet } as Station & { fleet: 'online' | 'offline' | 'none' };
  });
  const matchedHosts = new Set(stationRows.flatMap((station) => [hostKey(station.name), hostKey(station.dns)]).filter(Boolean));
  const fleetOnly = fleetAgents.filter((agent) => {
    const key = hostKey(agent.hostname ?? '');
    if (!key || matchedHosts.has(key)) return false;
    if (!needle) return true;
    return (agent.hostname ?? '').toLowerCase().includes(needle) || (agent.os ?? '').toLowerCase().includes(needle);
  });
  const enabledCount = users.filter((user) => user.enabled === true).length;
  const disabledCount = users.filter((user) => user.enabled === false).length;

  const kindLabel = (kind: Kind) => t(`people.kind.${kind}`);
  const kindShort = (kind: string) => t(`people.kindShort.${kind}`);
  const passwordText = (state?: PasswordState) => {
    if (state === 'valid') return t('people.passwordValid');
    if (state === 'expired') return t('people.passwordExpired');
    if (state === 'must_change') return t('people.passwordMustChange');
    if (state === 'none') return t('people.passwordNone');
    return t('people.unknown');
  };
  const passwordVariant = (state?: PasswordState): 'success' | 'warning' | 'default' =>
    state === 'valid' ? 'success' : state === 'expired' || state === 'must_change' ? 'warning' : 'default';
  const passwordEnd = (user: Person) =>
    user.password_never_expires ? t('people.passwordNever') : (formatDay(user.password_expires_at) || '—');

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['people-users'] });
    queryClient.invalidateQueries({ queryKey: ['people-groups'] });
    queryClient.invalidateQueries({ queryKey: ['people-stations'] });
    queryClient.invalidateQueries({ queryKey: ['people-group-members'] });
    queryClient.invalidateQueries({ queryKey: ['people-candidates'] });
  };

  const openPassword = (person: Person) => {
    const targets = person.id === '__bulk__' ? selectedPeople : [person];
    const mode = defaultPasswordMode(targets);
    setPassMode(mode);
    setCustomPassword(makePassword(mode, { words, complexity }));
    setPasswordDone([]);
    setUniqueEach(person.id === '__bulk__');
    setTemporary(active?.kind !== 'entity');
    setRejected(null);
    setRowErrors([]);
    setPasswordFor(person);
  };

  const closePassword = () => {
    setPasswordFor(null);
    setPasswordDone([]);
    setCustomPassword('');
    setRejected(null);
    setRowErrors([]);
  };

  const openCreate = () => {
    setPassMode('strong');
    setCustomPassword(makePassword('strong', { complexity }));
    setUniqueEach(true);
    setTemporary(active?.kind !== 'entity');
    setRejected(null);
    setRowErrors([]);
    if (!importText.trim()) {
      setForm({ login: '', first: '', last: '', email: '' });
      setCreateMode('one');
    } else setCreateMode('table');
    setCreating(true);
  };

  const withPasswords = (rows: ImportedAccount[]) => {
    const shared = customPassword || makePassword(passMode === 'manual' ? 'strong' : passMode, { words, complexity });
    const many = rows.length > 1 && uniqueEach && passMode !== 'manual';
    return rows.map((row) => ({
      ...row,
      password: row.password || (many ? makePassword(passMode, { words, complexity }) : shared),
    }));
  };

  /** Kontrola hesla na serveru (passwords.check) — stejná pravidla jako při uložení. */
  const checkTool = (args: Record<string, unknown>) => call<PasswordCheckResult>('passwords.check', args);

  // Minimum hesla podle politiky adresáře (AD minPwdLength, Keycloak length(n), jinak náhradní).
  const policyQ = useQuery({
    queryKey: ['people-password-policy', active?.tool, active?.id],
    queryFn: () =>
      call<{ ok?: boolean; min_length?: number; min_length_source?: string }>(active!.tool, {
        action: 'password_policy',
        source: active!.id,
      }),
    enabled: Boolean(active && (passwordFor || creating)),
    staleTime: 60_000,
  });
  const policyMin = policyQ.data?.ok !== false ? policyQ.data?.min_length : undefined;
  const policySource = policyQ.data?.ok !== false ? policyQ.data?.min_length_source : undefined;

  const openPerson = (user: Person) => {
    setOpened(user);
    setDraft(draftFrom(user));
    setMore(false);
  };

  const openGroup = (group: DirGroup) => {
    setOpenedGroup(group);
    setGroupDraft(group.description ?? '');
    setAddQuery('');
  };

  const passwordMut = useMutation({
    mutationFn: (vars: { person: Person; password: string; temporary: boolean }) =>
      call<ToolOk>(active!.tool, {
        action: 'password',
        source: active!.id,
        id: vars.person.id,
        password: vars.password || undefined,
        temporary: vars.temporary,
        // Jen kontext kontroly hesla; u action=password se profil nezapisuje.
        context_login: vars.person.login || undefined,
        display_name: vars.person.display_name || undefined,
        email: vars.person.email || undefined,
      }).then(unwrap),
    onSuccess: (data, vars) => {
      const value = data.password || vars.password;
      // Výsledek zůstane v dialogu: velké heslo, kopírovat, vytisknout lísteček.
      setPasswordDone([{ login: vars.person.login, name: vars.person.display_name, password: value }]);
      invalidate();
      toast.success(t('people.passwordSet'));
    },
    onError: (error: Error) => {
      if (error instanceof PasswordRejected) {
        setRejected(error.reasons);
        toast.error(t('people.passwordRejected'));
        return;
      }
      toast.error(error.message);
    },
  });

  const createMut = useMutation({
    mutationFn: async (vars: { rows: ImportedAccount[]; mode: 'one' | 'table'; accept?: boolean }) => {
      const rows = vars.rows;
      const unverified: ImportedAccount[] = [];
      const issuedRows: Array<{ login: string; name: string; password: string }> = [];
      const errors: Array<{ login: string; stage: 'create' | 'assign'; message: string; reasons?: PasswordReasonView[] }> = [];
      for (const row of rows) {
        try {
          const data = await call<ToolOk>(active!.tool, {
            action: 'create',
            source: active!.id,
            login: row.login,
            first_name: row.first,
            last_name: row.last,
            email: row.email || undefined,
            password: row.password,
            temporary: active?.kind === 'entity' ? false : temporary,
            // Souhlas s heslem, které nešlo ověřit proti únikům (jen po potvrzení v dialogu).
            accept_unverified: vars.accept || undefined,
          }).then(unwrap);
          const value = data.password || row.password;
          const name = [row.first, row.last].filter(Boolean).join(' ') || row.login;
          if (value) issuedRows.push({ login: row.login, name, password: value });
          if (row.group) {
            try {
              await call<ToolOk>(active!.tool, {
                action: 'assign',
                source: active!.id,
                id: data.user?.id || row.login,
                group: row.group,
              }).then(unwrap);
            } catch (error) {
              errors.push({ login: row.login, stage: 'assign', message: (error as Error).message });
            }
          }
        } catch (error) {
          if (
            error instanceof PasswordRejected && error.code === 'password_unverified' &&
            error.consentPossible && !vars.accept
          ) {
            unverified.push(row);
            continue;
          }
          errors.push({
            login: row.login,
            stage: 'create',
            message: (error as Error).message,
            reasons: error instanceof PasswordRejected ? error.reasons : undefined,
          });
        }
      }
      return { issued: issuedRows, errors, unverified };
    },
    onSuccess: (data, vars) => {
      const rows = vars.rows;
      if (data.issued.length > 0) {
        // Druhé kolo (po souhlasu) přidává k už vydaným, nepřepisuje je.
        setIssued((current) => vars.accept ? [...current, ...data.issued] : data.issued);
        setSecret(null);
        setCopied(false);
      }
      if (data.unverified.length > 0) setConsent({ rows: data.unverified, mode: vars.mode });
      const failed = rows.filter((row) =>
        data.unverified.includes(row) ||
        data.errors.some((item) => item.stage === 'create' && item.login === row.login)
      );
      if (failed.length > 0 && vars.mode === 'table') {
        setImportText([
          IMPORT_TEMPLATE.trim(),
          ...failed.map((row) => [row.login, row.first, row.last, row.email, row.password, row.group].map(csvCell).join(';')),
        ].join('\r\n'));
      } else if (failed.length === 0) {
        setCreating(false);
        setForm({ login: '', first: '', last: '', email: '' });
        setImportText('');
        setCustomPassword('');
      }
      invalidate();
      // Odmítnuté heslo jednoho účtu se ukáže u hesla, řádky tabulky v seznamu.
      const single = vars.mode === 'one' ? data.errors.find((item) => item.reasons) : undefined;
      setRejected(single?.reasons ?? null);
      setRowErrors(vars.mode === 'table' ? data.errors.map((item) => ({ login: item.login, message: item.message })) : []);
      if (data.errors.length) {
        toast.error(data.errors.slice(0, 3).map((item) => `${item.login}: ${item.message}`).join('\n'));
      } else if (data.unverified.length === 0) {
        toast.success(data.issued.length > 1 ? t('people.bulkDone') : t('people.created'));
      }
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const enableMut = useMutation({
    mutationFn: (vars: { person: Person; enabled: boolean }) =>
      call<ToolOk>(active!.tool, {
        action: 'set',
        source: active!.id,
        id: vars.person.id,
        enabled: vars.enabled,
      }).then(unwrap),
    onSuccess: (_data, vars) => {
      setOpened((current) => current && current.id === vars.person.id
        ? { ...current, enabled: vars.enabled }
        : current);
      invalidate();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const stationMut = useMutation({
    mutationFn: (vars: { station: Station; enabled: boolean }) =>
      call<ToolOk>(active!.tool, {
        action: 'set',
        source: active!.id,
        id: vars.station.id,
        enabled: vars.enabled,
      }).then(unwrap),
    onSuccess: (_data, vars) => {
      setOpenedStation((current) => current && current.id === vars.station.id
        ? { ...current, enabled: vars.enabled }
        : current);
      invalidate();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const saveMut = useMutation({
    mutationFn: (vars: { id: string; patch: Record<string, string> }) =>
      call<ToolOk>(active!.tool, {
        action: 'set',
        source: active!.id,
        id: vars.id,
        ...vars.patch,
      }).then(unwrap),
    onSuccess: (_data, vars) => {
      setOpened((current) => current && current.id === vars.id ? { ...current, ...vars.patch } : current);
      setDraft((current) => current ? { ...current, ...vars.patch } : current);
      invalidate();
      toast.success(t('people.saved', 'Uloženo'));
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const groupSaveMut = useMutation({
    mutationFn: (vars: { name: string; description: string }) =>
      call<ToolOk>(active!.tool, {
        action: 'set',
        source: active!.id,
        group: vars.name,
        description: vars.description,
      }).then(unwrap),
    onSuccess: (_data, vars) => {
      setOpenedGroup((current) => current && current.name === vars.name
        ? { ...current, description: vars.description }
        : current);
      invalidate();
      toast.success(t('people.saved', 'Uloženo'));
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const choose = (source: Source) => {
    const key = sourceKey(source);
    setActiveKey(key);
    localStorage.setItem(STORAGE_KEY, key);
    setOpened(null);
    setDraft(null);
    setMore(false);
    setOpenedGroup(null);
    setOpenedStation(null);
    setAddQuery('');
    setPicked([]);
    setIssued([]);
    setSecret(null);
    setCreating(false);
    setImportText('');
    setQuery('');
    setGroupFilter('');
    setPlaceFilter('');
    setQuick('all');
    setField('all');
    setStatus('');
  };

  const selectedPeople = users.filter((user) => picked.includes(user.id));

  const togglePick = (id: string) => {
    setPicked((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  };

  const bulkMut = useMutation({
    mutationFn: async (vars: {
      action: 'password' | 'unlock' | 'set' | 'assign' | 'unassign';
      enabled?: boolean;
      password?: string;
      temporary?: boolean;
      group?: string;
      ids?: string[];
      passwords?: Record<string, string>;
    }) => {
      const wanted = vars.ids ?? picked;
      const pool = [
        ...loaded,
        ...(opened ? [opened] : []),
        ...(membersQ.data?.users ?? []),
        ...(candidatesQ.data?.users ?? []),
      ];
      const targets = wanted
        .map((id) => pool.find((person) => person.id === id))
        .filter((person): person is Person => Boolean(person));
      const lines: string[] = [];
      const errors: string[] = [];
      const failedRows: Array<{ login: string; message: string }> = [];
      const issuedRows: Array<{ login: string; name: string; password: string }> = [];
      if (wanted.length > 0 && targets.length === 0) errors.push('user_not_found');
      for (const person of targets) {
        try {
          const sent = vars.passwords?.[person.id] || vars.password || '';
          const data = await call<ToolOk>(active!.tool, {
            action: vars.action,
            source: active!.id,
            id: person.id,
            password: sent || undefined,
            temporary: vars.temporary,
            enabled: vars.enabled,
            group: vars.group,
            ...(vars.action === 'password'
              ? {
                context_login: person.login || undefined,
                display_name: person.display_name || undefined,
                email: person.email || undefined,
              }
              : {}),
          }).then(unwrap);
          const value = data.password || sent;
          if (value) {
            lines.push(`${person.login} · ${value}`);
            issuedRows.push({ login: person.login, name: person.display_name, password: value });
          }
        } catch (error) {
          errors.push(`${person.login}: ${(error as Error).message}`);
          failedRows.push({ login: person.login, message: (error as Error).message });
        }
      }
      return { lines, errors, count: targets.length, issued: issuedRows, failedRows };
    },
    onSuccess: (data, vars) => {
      if (vars.action === 'password') {
        // Hromadná hesla zůstanou v dialogu k vytištění / stažení; neúspěšné
        // řádky s důvodem (např. odmítnuté heslo) zůstanou vidět vedle nich.
        setPasswordDone(data.issued);
        setRowErrors(data.failedRows);
        setPicked([]);
        invalidate();
        if (data.errors.length) toast.error(data.errors.slice(0, 3).join('\n'));
        else toast.success(t('people.bulkDone'));
        return;
      }
      if (vars.action === 'unlock') {
        invalidate();
        setOpened((current) => current && (vars.ids ?? picked).includes(current.id) ? { ...current, locked: false } : current);
        if (!vars.ids) setPicked([]);
        if (data.errors.length) toast.error(data.errors.slice(0, 3).join('\n'));
        else toast.success(t('people.unlocked', 'Účet je odemčený'));
        return;
      }
      if (data.lines.length === 1) {
        const [login, password] = data.lines[0]!.split(' · ');
        setSecret({ login: login || '', password: password || '' });
        setCopied(false);
      } else if (data.lines.length > 1) {
        setSecret({ login: String(data.lines.length), password: data.lines.join('\n') });
        setCopied(false);
      }
      setAssignOpen(false);
      setGroupName('');
      setPasswordFor(null);
      setCustomPassword('');
      if (data.issued.length > 0) {
        setIssued(data.issued);
        setSecret(null);
        setCopied(false);
      }
      setPicked([]);
      if (vars.action === 'assign' && vars.group) {
        const added = vars.group;
        const ids = vars.ids ?? picked;
        setOpened((current) => current && ids.includes(current.id)
          ? { ...current, groups: [...new Set([...(current.groups ?? []), added])] }
          : current);
      }
      if (vars.action === 'unassign' && vars.group) {
        const gone = vars.group;
        setOpened((current) => current && (vars.ids ?? picked).includes(current.id)
          ? { ...current, groups: (current.groups ?? []).filter((group) => group !== gone) }
          : current);
      }
      invalidate();
      const singleMembership = (vars.ids?.length ?? 0) === 1 && (vars.action === 'assign' || vars.action === 'unassign');
      if (singleMembership && vars.action === 'assign') setAddQuery('');
      if (data.errors.length) toast.error(data.errors.slice(0, 3).join('\n'));
      else if (!singleMembership) toast.success(t('people.bulkDone'));
    },
  });

  const copySecret = async () => {
    if (!secret) return;
    await navigator.clipboard.writeText(secret.password);
    setCopied(true);
  };

  const canWrite = Boolean(active?.available && active.can_write);
  const imported = parseAccountTable(importText);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 gap-2 overflow-x-auto">
          {sources.map((source) => {
            const selected = active ? sourceKey(active) === sourceKey(source) : false;
            return (
              <button
                key={sourceKey(source)}
                type="button"
                onClick={() => choose(source)}
                className={`flex h-10 shrink-0 items-center gap-2 rounded-lg border px-3 text-sm transition ${
                  selected ? 'border-plex bg-plex/15 text-white' : 'border-white/10 bg-white/5 text-muted-foreground hover:bg-white/10'
                }`}
              >
                <KindIcon kind={source.kind} className="h-3.5 w-3.5" />
                <span className="max-w-[16rem] truncate">{sourceChipLabel(source, kindShort)}</span>
                <span className={`h-1.5 w-1.5 rounded-full ${source.available ? (source.can_write ? 'bg-emerald-400' : 'bg-sky-400') : 'bg-amber-400'}`} />
              </button>
            );
          })}
        </div>
        <Button variant="primary" size="md" className="h-10" icon={UserPlus} disabled={!canWrite} onClick={openCreate}>
          {t('people.create')}
        </Button>
      </div>

      {active && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>{kindLabel(active.kind)}{active.detail ? ` · ${active.detail}` : ''}</span>
          <Badge variant={active.available ? (active.can_write ? 'success' : 'info') : 'warning'}>
            {active.available ? (active.can_write ? t('people.canWrite') : t('people.readOnly')) : t('people.unavailable')}
          </Badge>
          {active.available && pane === 'people' && (
            <>
              <span>{t('people.users')}: {users.length}</span>
              <span>{t('people.enabled')}: {enabledCount}</span>
              <span>{t('people.disabled')}: {disabledCount}</span>
            </>
          )}
          {active.write_mode === 'personal' && active.master_login && (
            <>
              <Badge variant="info">{t('people.writingAs', { login: active.master_login, defaultValue: 'Zapisuješ jako {{login}}' })}</Badge>
            </>
          )}
        </div>
      )}
      {active?.available && active.write_mode === 'personal' && !active.master_login && (
        <Alert variant="info">
          <p className="text-sm">
            {t('people.personalOnly', 'Změny v AD (hesla, odemčení, skupiny) se dělají pod účtem z AD, kterým jsi přihlášený do ACM. S tímto přihlášením jde jen prohlížet.')}
          </p>
          {active.master_reason && <p className="mt-1 text-xs text-muted-foreground">{active.master_reason}</p>}
        </Alert>
      )}

      {secret && (
        <Card className="border-plex/40">
          <CardContent className="flex flex-col gap-3 py-4 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm font-medium">{t('people.passwordOnce')}</p>
              <p className="mt-1 font-mono text-sm">
                {secret.login} · <span className="select-all">{secret.password}</span>
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" icon={copied ? Check : Copy} onClick={() => void copySecret()}>
                {copied ? t('people.copied') : t('people.copy')}
              </Button>
              <Button variant="ghost" onClick={() => setSecret(null)}>{t('people.close')}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {issued.length > 0 && (
        <Card className="border-plex/40">
          <CardContent className="space-y-3 py-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-medium">{t('people.issuedTitle', 'Účty v tabulce')}</p>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  icon={Copy}
                  onClick={() => {
                    const text = [
                      [t('people.login'), t('people.name'), t('people.password')],
                      ...issued.map((row) => [row.login, row.name, row.password]),
                    ].map((line) => line.join('\t')).join('\n');
                    void navigator.clipboard.writeText(text).then(() => {
                      setCopied(true);
                      toast.success(t('people.copied'));
                    });
                  }}
                >{copied ? t('people.copied') : t('people.copy')}</Button>
                <Button
                  variant="primary"
                  onClick={() => downloadTable(issued, [t('people.login'), t('people.name'), t('people.password')])}
                >{t('people.exportTable', 'Stáhnout tabulku')}</Button>
                <Button variant="ghost" onClick={() => setIssued([])}>{t('people.close')}</Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">{t('people.passwordOnce')}</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-2 py-2 font-medium">{t('people.login')}</th>
                    <th className="px-2 py-2 font-medium">{t('people.name')}</th>
                    <th className="px-2 py-2 font-medium">{t('people.password')}</th>
                  </tr>
                </thead>
                <tbody>
                  {issued.map((row) => (
                    <tr key={row.login} className="border-t border-white/10">
                      <td className="px-2 py-2 font-mono">{row.login}</td>
                      <td className="px-2 py-2">{row.name}</td>
                      <td className="px-2 py-2 font-mono select-all">{row.password}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
          <div className="flex gap-1 border-b border-white/10">
            <button
              type="button"
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${pane === 'people' ? 'border-plex text-plex' : 'border-transparent text-muted-foreground'}`}
              onClick={() => setPane('people')}
            >{t('people.panePeople')}</button>
            <button
              type="button"
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${pane === 'groups' ? 'border-plex text-plex' : 'border-transparent text-muted-foreground'}`}
              onClick={() => setPane('groups')}
            >{t('people.paneGroups')}</button>
            <button
              type="button"
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${pane === 'stations' ? 'border-plex text-plex' : 'border-transparent text-muted-foreground'}`}
              onClick={() => setPane('stations')}
            >{t('people.paneStations', 'Stanice')}</button>
          </div>

          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <SearchInput
              value={query}
              onChange={setQuery}
              placeholder={t('people.search')}
              size="md"
              className="lg:max-w-sm"
              resultCount={active?.available ? (pane === 'people' ? users.length : pane === 'groups' ? shownGroups.length : stationRows.length + fleetOnly.length) : undefined}
            />
            {pane === 'people' && active?.available && (
              <div className="grid flex-1 gap-2 sm:grid-cols-2">
                {places.length > 0 && (
                  <Select
                    value={placeFilter}
                    onChange={(event) => setPlaceFilter(event.target.value)}
                    aria-label={t('people.placeFilter', 'Třída nebo oddělení')}
                    searchable
                    searchPlaceholder={t('people.search')}
                    options={[
                      { value: '', label: t('people.anyPlace', 'Všechny třídy a oddělení') },
                      ...places.map((place) => ({ value: place.id, label: place.path })),
                    ]}
                  />
                )}
                <Select
                  value={groupFilter}
                  onChange={(event) => setGroupFilter(event.target.value)}
                  aria-label={t('people.groups')}
                  searchable
                  searchPlaceholder={t('people.search')}
                  options={[
                    { value: '', label: t('people.anyGroup') },
                    ...directoryGroups.map((group) => ({ value: group.name, label: group.name })),
                  ]}
                />
              </div>
            )}
            {pane === 'stations' && active?.available && (
              <div className="grid flex-1 gap-2 sm:grid-cols-2">
                <Select value={field} onChange={(event) => setField(event.target.value as Field)} options={[
                  { value: 'all', label: t('people.fieldAll') },
                  { value: 'name', label: t('people.name') },
                  { value: 'login', label: t('people.login') },
                  { value: 'email', label: t('people.email') },
                  { value: 'place', label: t('people.place') },
                  { value: 'department', label: t('people.department') },
                  { value: 'group', label: t('people.groups') },
                ]} />
                <Select value={status} onChange={(event) => setStatus(event.target.value as '' | 'on' | 'off')} options={[
                  { value: '', label: t('people.statusAll') },
                  { value: 'on', label: t('people.on') },
                  { value: 'off', label: t('people.off') },
                ]} />
              </div>
            )}
          </div>
          {pane === 'people' && active?.available && (
            <FilterChips
              label={t('people.quickLabel', 'Rychlý výběr')}
              value={quick}
              onChange={setQuick}
              options={[
                { value: 'all', label: t('people.quickAll', 'Všichni'), count: loaded.length },
                { value: 'locked', label: t('people.quickLocked', 'Zamčení'), count: quickCount('locked') },
                { value: 'must_change', label: t('people.quickMustChange', 'Čeká na změnu hesla'), count: quickCount('must_change') },
                { value: 'expired', label: t('people.quickExpired', 'Vypršelé heslo'), count: quickCount('expired') },
                { value: 'expiring', label: t('people.quickExpiring', 'Heslo vyprší do 14 dní'), count: quickCount('expiring') },
                { value: 'disabled', label: t('people.quickDisabled', 'Vypnutí'), count: quickCount('disabled') },
                { value: 'inactive', label: t('people.quickInactive', 'Nepřihlášen 90+ dní'), count: quickCount('inactive') },
              ]}
            />
          )}
          {active && !active.available && active.error && (
            <p className="text-sm text-amber-300">{active.error}</p>
          )}

          {!active && <EmptyState icon={Users} title={t('people.noSources')} />}
          {active && !active.available && (
            <EmptyState icon={Users} title={t('people.unavailable')} description={active.error} />
          )}

          {pane === 'groups' && active?.available && groupsQ.isLoading && (
            <p className="py-8 text-center text-sm text-muted-foreground">{t('people.loading')}</p>
          )}
          {pane === 'groups' && active?.available && groupsQ.isError && (
            <EmptyState icon={Users} title={t('people.loadFailed')} description={(groupsQ.error as Error).message} />
          )}
          {pane === 'groups' && active?.available && groupsQ.isSuccess && shownGroups.length === 0 && (
            <EmptyState icon={Users} title={t('people.noGroups')} />
          )}
          {pane === 'groups' && shownGroups.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('people.name')}</th>
                    <th className="px-3 py-2 font-medium">{t('people.members')}</th>
                    <th className="px-3 py-2 font-medium">{t('people.kindLabel')}</th>
                  </tr>
                </thead>
                <tbody>
                  {shownGroups.map((group) => (
                    <tr
                      key={group.id || group.name}
                      className="cursor-pointer border-t border-white/10 hover:bg-white/[0.04]"
                      onClick={() => openGroup(group)}
                    >
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-3">
                          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/5 text-muted-foreground">
                            <Users className="h-4 w-4" />
                          </span>
                          <span className="min-w-0">
                            <span className="block truncate font-medium">{group.name}</span>
                            <span className="block truncate text-xs text-muted-foreground">{group.description || '—'}</span>
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-3">{group.members ?? '—'}</td>
                      <td className="px-3 py-3">{group.kind ? <Badge>{group.kind}</Badge> : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {pane === 'stations' && active?.available && stationsQ.isLoading && (
            <p className="py-8 text-center text-sm text-muted-foreground">{t('people.loading')}</p>
          )}
          {pane === 'stations' && active?.available && stationsQ.isError && (
            <EmptyState icon={Monitor} title={t('people.loadFailed')} description={(stationsQ.error as Error).message} />
          )}
          {pane === 'stations' && active?.available && stationsQ.isSuccess && stationRows.length === 0 && fleetOnly.length === 0 && (
            <EmptyState icon={Monitor} title={debounced ? t('people.emptyQuery') : t('people.noStations', 'V tomhle adresáři žádná stanice není')} />
          )}
          {pane === 'stations' && stationRows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('people.name')}</th>
                    <th className="px-3 py-2 font-medium">{t('people.stationDns', 'DNS')}</th>
                    <th className="px-3 py-2 font-medium">{t('people.stationOs', 'Systém')}</th>
                    <th className="px-3 py-2 font-medium">{t('people.place')}</th>
                    <th className="px-3 py-2 font-medium">{t('people.signedIn', 'Přihlášený')}</th>
                    <th className="px-3 py-2 font-medium">RustDesk</th>
                    <th className="px-3 py-2 font-medium">Fleet</th>
                    <th className="px-3 py-2 font-medium">{t('people.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {stationRows.map((station) => (
                    <tr
                      key={station.id}
                      className="cursor-pointer border-t border-white/10 hover:bg-white/[0.04]"
                      onClick={() => setOpenedStation(station)}
                    >
                      <td className="px-3 py-3">
                        <span className="block truncate font-medium">{station.name}</span>
                        <span className="block truncate text-xs text-muted-foreground">{t(`people.machine.${station.kind}`, station.kind)}</span>
                      </td>
                      <td className="px-3 py-3 font-mono text-xs">{station.dns || '—'}</td>
                      <td className="px-3 py-3">{station.os || '—'}</td>
                      <td className="px-3 py-3">{station.place || '—'}</td>
                      <td className="px-3 py-3"><SignedIn sessions={sessionsFor(station.name, station.dns)} /></td>
                      <td className="px-3 py-3" onClick={(event) => event.stopPropagation()}><DeskLink id={deskFor(station.name, station.dns)} /></td>
                      <td className="px-3 py-3">
                        <Badge variant={station.fleet === 'online' ? 'success' : station.fleet === 'offline' ? 'warning' : 'default'}>
                          {station.fleet === 'online' ? t('people.fleetOnline', 'Ve fleetu online') : station.fleet === 'offline' ? t('people.fleetOffline', 'Ve fleetu offline') : t('people.fleetNone', 'Ve fleetu není')}
                        </Badge>
                      </td>
                      <td className="px-3 py-3" onClick={(event) => event.stopPropagation()}>
                        {canWrite ? (
                          <Toggle
                            value={station.enabled !== false}
                            label={station.enabled === false ? t('people.off') : t('people.on')}
                            saving={stationMut.isPending}
                            onSave={async (enabled) => {
                              await stationMut.mutateAsync({ station, enabled });
                            }}
                          />
                        ) : (station.enabled === false ? t('people.off') : t('people.on'))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {pane === 'stations' && fleetOnly.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">{t('people.fleetOnly', 'Ve fleetu, v adresáři ne')}</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <tbody>
                    {fleetOnly.map((agent) => (
                      <tr
                        key={agent.agent_id || agent.hostname}
                        className="cursor-pointer border-t border-white/10 hover:bg-white/[0.04]"
                        onClick={() => setOpenedStation({
                          id: `fleet:${agent.agent_id || agent.hostname || ''}`,
                          name: agent.hostname || '',
                          dns: agent.hostname || '',
                          os: [agent.os, agent.os_version].filter(Boolean).join(' '),
                          enabled: true,
                          place: '',
                          origin: 'fleet',
                          description: '',
                          kind: 'agent',
                        })}
                      >
                        <td className="px-3 py-3 font-medium">{agent.hostname}</td>
                        <td className="px-3 py-3">{[agent.os, agent.os_version].filter(Boolean).join(' ') || '—'}</td>
                        <td className="px-3 py-3"><SignedIn sessions={sessionsFor(agent.hostname || '')} /></td>
                        <td className="px-3 py-3" onClick={(event) => event.stopPropagation()}><DeskLink id={deskFor(agent.hostname || '')} /></td>
                        <td className="px-3 py-3">
                          <Badge variant={agent.status === 'online' ? 'success' : 'warning'}>
                            {agent.status === 'online' ? t('people.fleetOnline', 'Ve fleetu online') : t('people.fleetOffline', 'Ve fleetu offline')}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {pane === 'people' && active?.available && usersQ.isLoading && (
            <p className="py-8 text-center text-sm text-muted-foreground">{t('people.loading')}</p>
          )}
          {pane === 'people' && active?.available && usersQ.isError && (
            <EmptyState icon={Users} title={t('people.loadFailed')} description={(usersQ.error as Error).message} />
          )}
          {pane === 'people' && active?.available && usersQ.isSuccess && users.length === 0 && (
            <EmptyState icon={Users} title={debounced ? t('people.emptyQuery') : t('people.emptyTitle')} />
          )}

          {pane === 'people' && users.length > 0 && (
            <>
              {canWrite && selectedPeople.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 rounded-xl bg-plex/10 px-3 py-2">
                  <span className="text-sm">{t('people.selected', { count: selectedPeople.length })}</span>
                  <Button variant="secondary" size="md" className="h-10" icon={KeyRound} loading={bulkMut.isPending} onClick={() => {
                    openPassword({ id: '__bulk__', login: '', display_name: t('people.selected', { count: selectedPeople.length }), email: '', enabled: null, place: '', origin: '' });
                  }}>{t('people.bulkPassword')}</Button>
                  {selectedPeople.some((person) => person.locked) && (
                    <Button variant="secondary" size="md" className="h-10" icon={LockOpen} loading={bulkMut.isPending} onClick={() => {
                      bulkMut.mutate({ action: 'unlock', ids: selectedPeople.filter((person) => person.locked).map((person) => person.id) });
                    }}>{t('people.unlockSelected', 'Odemknout zamčené')}</Button>
                  )}
                  <Button variant="secondary" size="md" className="h-10" loading={bulkMut.isPending} onClick={() => bulkMut.mutate({ action: 'set', enabled: true })}>{t('people.enableSelected')}</Button>
                  <Button variant="secondary" size="md" className="h-10" loading={bulkMut.isPending} onClick={() => bulkMut.mutate({ action: 'set', enabled: false })}>{t('people.disableSelected')}</Button>
                  <Button variant="secondary" size="md" className="h-10" loading={bulkMut.isPending} onClick={() => { setAssignIds(null); setGroupName(groupFilter); setAssignOpen(true); }}>{t('people.assign')}</Button>
                  {groupFilter && (
                    <Button variant="secondary" size="md" className="h-10" loading={bulkMut.isPending} onClick={() => bulkMut.mutate({ action: 'unassign', group: groupFilter })}>{t('people.unassign')}</Button>
                  )}
                </div>
              )}
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="w-8 px-3 py-2">
                        <input
                          type="checkbox"
                          checked={picked.length === users.length && users.length > 0}
                          onChange={() => setPicked(picked.length === users.length ? [] : users.map((user) => user.id))}
                        />
                      </th>
                      <th className="px-3 py-2 font-medium">{t('people.name')}</th>
                      <th className="px-3 py-2 font-medium">{t('people.email')}</th>
                      <th className="px-3 py-2 font-medium">{t('people.department')}</th>
                      <th className="px-3 py-2 font-medium">{t('people.passwordState')}</th>
                      <th className="px-3 py-2 font-medium">{t('people.groups')}</th>
                      <th className="px-3 py-2 font-medium">{t('people.status')}</th>
                      {canWrite && <th className="px-3 py-2 font-medium"><span className="sr-only">{t('people.actions', 'Akce')}</span></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr
                        key={user.id}
                        className="cursor-pointer border-t border-white/10 hover:bg-white/[0.04]"
                        onClick={() => openPerson(user)}
                      >
                        <td className="px-3 py-3" onClick={(event) => event.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={picked.includes(user.id)}
                            onChange={() => togglePick(user.id)}
                          />
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-3">
                            <EntityAvatar label={user.display_name || user.login} size={32} />
                            <span className="min-w-0">
                              <span className="block truncate font-medium">{user.display_name}</span>
                              <span className="block truncate font-mono text-xs text-muted-foreground">{user.login}</span>
                            </span>
                          </div>
                        </td>
                        <td className="max-w-[14rem] truncate px-3 py-3">{user.email || '—'}</td>
                        <td className="max-w-[10rem] truncate px-3 py-3">{user.department || '—'}</td>
                        <td className="px-3 py-3">
                          <Badge variant={passwordVariant(user.password_state)}>{passwordText(user.password_state)}</Badge>
                        </td>
                        <td className="max-w-[14rem] truncate px-3 py-3" title={(user.groups ?? []).join(', ')}>
                          {groupPreview(user.groups) || '—'}
                        </td>
                        <td className="px-3 py-3">
                          <span className="inline-flex flex-wrap gap-1">
                            <Badge variant={user.enabled === false ? 'warning' : 'success'}>
                              {user.enabled === false ? t('people.off') : user.enabled === true ? t('people.on') : t('people.unknown')}
                            </Badge>
                            {user.locked ? <Badge variant="warning">{t('people.locked')}</Badge> : null}
                          </span>
                        </td>
                        {canWrite && (
                          <td className="px-3 py-3" onClick={(event) => event.stopPropagation()}>
                            <span className="flex justify-end gap-2">
                              {user.locked && (
                                <Button
                                  size="sm"
                                  variant="warning"
                                  icon={LockOpen}
                                  loading={bulkMut.isPending}
                                  onClick={() => bulkMut.mutate({ action: 'unlock', ids: [user.id] })}
                                >{t('people.unlock', 'Odemknout')}</Button>
                              )}
                              <Button size="sm" variant="secondary" icon={KeyRound} onClick={() => openPassword(user)}>
                                {t('people.changePassword', 'Změnit heslo')}
                              </Button>
                            </span>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="space-y-2 md:hidden">
                {users.map((user) => (
                  <div key={user.id} className="flex items-center gap-3 rounded-xl border border-white/10 px-3 py-3">
                    {canWrite && (
                      <input
                        type="checkbox"
                        checked={picked.includes(user.id)}
                        onChange={() => togglePick(user.id)}
                      />
                    )}
                    <button type="button" className="flex min-w-0 flex-1 items-center gap-3 text-left" onClick={() => openPerson(user)}>
                      <EntityAvatar label={user.display_name || user.login} size={36} />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center justify-between gap-2">
                          <span className="truncate font-medium">{user.display_name}</span>
                          <Badge variant={user.enabled === false ? 'warning' : 'success'}>
                            {user.enabled === false ? t('people.off') : user.enabled === true ? t('people.on') : t('people.unknown')}
                          </Badge>
                        </span>
                        <span className="mt-0.5 block truncate font-mono text-xs text-muted-foreground">{user.login}</span>
                        <span className="mt-1 block truncate text-xs text-muted-foreground">
                          {passwordText(user.password_state)}
                          {user.department ? ` · ${user.department}` : ''}
                          {` · ${t('people.groups')}: ${(user.groups ?? []).length}`}
                        </span>
                      </span>
                    </button>
                    {canWrite && (
                      <span className="flex flex-col gap-2">
                        {user.locked && (
                          <Button
                            size="sm"
                            variant="warning"
                            icon={LockOpen}
                            loading={bulkMut.isPending}
                            onClick={() => bulkMut.mutate({ action: 'unlock', ids: [user.id] })}
                          >{t('people.unlock', 'Odemknout')}</Button>
                        )}
                        <Button size="sm" variant="secondary" icon={KeyRound} onClick={() => openPassword(user)}>
                          {t('people.passwordShort', 'Heslo')}
                        </Button>
                      </span>
                    )}
                  </div>
                ))}
              </div>
              {usersQ.data?.truncated && (
                <p className="text-xs text-muted-foreground">{t('people.truncated')}</p>
              )}
            </>
          )}
      </div>

      <Modal
        open={Boolean(opened)}
        onClose={() => setOpened(null)}
        size="xl"
        title={opened?.display_name || t('people.detail')}
        footer={opened && draft ? (
          <>
            {canWrite && opened.locked && (
              <Button
                variant="warning"
                icon={LockOpen}
                loading={bulkMut.isPending}
                onClick={() => bulkMut.mutate({ action: 'unlock', ids: [opened.id] })}
              >{t('people.unlock', 'Odemknout')}</Button>
            )}
            <Button
              variant="secondary"
              icon={KeyRound}
              disabled={!canWrite}
              onClick={() => openPassword(opened)}
            >{t('people.changePassword', 'Změnit heslo')}</Button>
            {canWrite && (
              <Button
                variant="primary"
                loading={saveMut.isPending}
                disabled={Object.keys(profileDelta(opened, draft)).length === 0}
                onClick={() => saveMut.mutate({ id: opened.id, patch: profileDelta(opened, draft) })}
              >{t('people.save', 'Uložit')}</Button>
            )}
          </>
        ) : undefined}
      >
        {opened && draft && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex min-w-0 items-center gap-4">
                <EntityAvatar label={opened.display_name || opened.login} size={56} />
                <div className="min-w-0">
                  <p className="truncate font-mono text-sm text-muted-foreground">{opened.login}</p>
                  <p className="mt-1 truncate text-xs text-muted-foreground">
                    {[opened.place, opened.origin].filter(Boolean).join(' · ') || '—'}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Badge variant={passwordVariant(opened.password_state)}>{passwordText(opened.password_state)}</Badge>
                    <span className="text-xs text-muted-foreground">{passwordEnd(opened)}</span>
                    {opened.locked ? <Badge variant="warning">{t('people.locked')}</Badge> : null}
                  </div>
                </div>
              </div>
              {canWrite && opened.enabled !== null && (
                <Toggle
                  value={opened.enabled === true}
                  label={opened.enabled ? t('people.on') : t('people.off')}
                  saving={enableMut.isPending}
                  onSave={async (enabled) => {
                    await enableMut.mutateAsync({ person: opened, enabled });
                  }}
                />
              )}
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <ProfileSection title={t('people.sectionAccount', 'Údaje účtu')}>
                <div className="grid gap-3 sm:grid-cols-2">
                  <ProfileField
                    label={t('people.name')}
                    value={draft.display_name}
                    editing={canWrite}
                    onChange={(value) => setDraft({ ...draft, display_name: value })}
                  />
                  <ProfileField
                    label={t('people.email')}
                    value={draft.email}
                    editing={canWrite}
                    onChange={(value) => setDraft({ ...draft, email: value })}
                  />
                </div>
              </ProfileSection>
              <ProfileSection
                title={t('people.groups')}
                extra={canWrite ? (
                  <Button variant="ghost" size="sm" onClick={() => {
                    setAssignIds([opened.id]);
                    setGroupName('');
                    setAssignOpen(true);
                  }}>{t('people.assign')}</Button>
                ) : undefined}
              >
                <div className="flex flex-wrap gap-2">
                  {(opened.groups ?? []).length === 0 && <span className="text-sm text-muted-foreground">—</span>}
                  {(opened.groups ?? []).map((group) => (
                    <span key={group} className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs">
                      {group}
                      {canWrite && (
                        <button
                          type="button"
                          className="rounded-full p-0.5 hover:bg-white/10"
                          disabled={bulkMut.isPending}
                          onClick={() => bulkMut.mutate({ action: 'unassign', group, ids: [opened.id] })}
                        >
                          <X className="h-3 w-3 opacity-70" />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              </ProfileSection>
            </div>

            <button
              type="button"
              className="flex w-full items-center justify-center rounded-xl border border-dashed border-white/15 py-2 text-sm text-muted-foreground hover:bg-white/5"
              onClick={() => setMore((value) => !value)}
            >
              {more ? t('people.showLess', 'Základ') : t('people.showRest', 'Všechny údaje')}
            </button>

            {more && (
              <div className="grid gap-4 lg:grid-cols-2">
                <ProfileSection title={t('people.sectionOrg', 'Organizace')}>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <ProfileField label={t('people.department')} value={draft.department} editing={canWrite} onChange={(value) => setDraft({ ...draft, department: value })} />
                    <ProfileField label={t('people.jobTitle')} value={draft.job_title} editing={canWrite} onChange={(value) => setDraft({ ...draft, job_title: value })} />
                    <ProfileField label={t('people.office')} value={draft.office} editing={canWrite} onChange={(value) => setDraft({ ...draft, office: value })} />
                    <ProfileField label={t('people.phone')} value={draft.phone} editing={canWrite} onChange={(value) => setDraft({ ...draft, phone: value })} />
                    <ProfileField label={t('people.mobile')} value={draft.mobile} editing={canWrite} onChange={(value) => setDraft({ ...draft, mobile: value })} />
                    <ProfileField className="sm:col-span-2" label={t('people.description')} value={draft.description} editing={canWrite} onChange={(value) => setDraft({ ...draft, description: value })} />
                  </div>
                </ProfileSection>
                <ProfileSection title={t('people.sectionSignIn', 'Přihlášení')}>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <ProfileField label={t('people.upn')} value={draft.upn} editing={canWrite} onChange={(value) => setDraft({ ...draft, upn: value })} />
                    <ProfileField label={t('people.homeDrive')} value={draft.home_drive} editing={canWrite} onChange={(value) => setDraft({ ...draft, home_drive: value })} />
                    <ProfileField className="sm:col-span-2" label={t('people.homeDirectory')} value={draft.home_directory} editing={canWrite} onChange={(value) => setDraft({ ...draft, home_directory: value })} />
                    <ProfileField className="sm:col-span-2" label={t('people.profilePath')} value={draft.profile_path} editing={canWrite} onChange={(value) => setDraft({ ...draft, profile_path: value })} />
                    <ProfileField className="sm:col-span-2" label={t('people.logonScript')} value={draft.logon_script} editing={canWrite} onChange={(value) => setDraft({ ...draft, logon_script: value })} />
                  </div>
                </ProfileSection>
                <ProfileSection className="lg:col-span-2" title={t('people.sectionState', 'Stav')}>
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                    <Fact label={t('people.passwordEnd')}>{passwordEnd(opened)}</Fact>
                    <Fact label={t('people.accountUntil')}>{formatDay(opened.account_expires_at) || '—'}</Fact>
                    <Fact label={t('people.lastLogon')}>{formatDay(opened.last_logon_at) || '—'}</Fact>
                    <Fact label={t('people.created')}>{formatDay(opened.created_at) || '—'}</Fact>
                    <Fact label={t('people.place')}>{opened.place || '—'}</Fact>
                    <Fact label={t('people.origin')}>{opened.origin || '—'}</Fact>
                    <Fact label={t('people.status')}>
                      {opened.locked ? <Badge variant="warning">{t('people.locked')}</Badge> : (opened.enabled === false ? t('people.off') : t('people.on'))}
                    </Fact>
                  </div>
                </ProfileSection>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        open={Boolean(openedGroup)}
        onClose={() => setOpenedGroup(null)}
        size="xl"
        title={openedGroup?.name || t('people.paneGroups')}
        footer={openedGroup && canWrite && active?.kind !== 'keycloak' ? (
          <Button
            variant="primary"
            loading={groupSaveMut.isPending}
            disabled={groupDraft === (openedGroup.description ?? '')}
            onClick={() => groupSaveMut.mutate({ name: openedGroup.name, description: groupDraft })}
          >{t('people.save', 'Uložit')}</Button>
        ) : undefined}
      >
        {openedGroup && (() => {
          const members = membersQ.data?.users ?? [];
          const memberNeedle = addQuery.trim().toLowerCase();
          const visibleMembers = memberNeedle
            ? members.filter((person) =>
              person.display_name.toLowerCase().includes(memberNeedle)
              || person.login.toLowerCase().includes(memberNeedle)
              || (person.email ?? '').toLowerCase().includes(memberNeedle))
            : members;
          const memberIds = new Set(members.flatMap((person) => [person.id, person.login]));
          const suggestions = canWrite
            ? (candidatesQ.data?.users ?? []).filter((person) => !memberIds.has(person.id) && !memberIds.has(person.login)).slice(0, 6)
            : [];
          const memberCount = membersQ.isSuccess ? members.length : openedGroup.members;
          return (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                {openedGroup.kind ? <Badge>{openedGroup.kind}</Badge> : null}
                <span className="text-sm text-muted-foreground">{t('people.members')}: {memberCount ?? '—'}</span>
              </div>
              <ProfileField
                label={t('people.description')}
                value={groupDraft}
                editing={canWrite && active?.kind !== 'keycloak'}
                onChange={setGroupDraft}
              />
              <ProfileSection
                title={t('people.memberList', 'Členové')}
                extra={(
                  <Button variant="ghost" size="sm" onClick={() => {
                    setGroupFilter(openedGroup.name);
                    setPane('people');
                    setOpenedGroup(null);
                  }}>{t('people.listMembers', 'Vypsat členy')}</Button>
                )}
              >
                <SearchInput
                  value={addQuery}
                  onChange={setAddQuery}
                  size="md"
                  placeholder={canWrite ? t('people.memberFind', 'Hledat nebo přidat') : t('people.search')}
                />
                {suggestions.length > 0 && (
                  <div className="mt-3 overflow-hidden rounded-xl border border-plex/30">
                    <p className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('people.addMember', 'Přidat člena')}</p>
                    {suggestions.map((person) => (
                      <button
                        key={person.id}
                        type="button"
                        className="flex w-full items-center gap-3 border-t border-white/10 px-3 py-2 text-left hover:bg-white/5"
                        disabled={bulkMut.isPending}
                        onClick={() => bulkMut.mutate({ action: 'assign', group: openedGroup.name, ids: [person.id] })}
                      >
                        <EntityAvatar label={person.display_name || person.login} size={28} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium">{person.display_name}</span>
                          <span className="block truncate font-mono text-xs text-muted-foreground">{person.login}</span>
                        </span>
                        <UserPlus className="h-4 w-4 shrink-0 text-plex" />
                      </button>
                    ))}
                  </div>
                )}
                <div className="mt-3 space-y-2">
                  {membersQ.isLoading && <p className="text-sm text-muted-foreground">{t('people.loading')}</p>}
                  {membersQ.isError && <p className="text-sm text-muted-foreground">{(membersQ.error as Error).message}</p>}
                  {membersQ.isSuccess && visibleMembers.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      {members.length === 0 ? t('people.noMembers', 'Ve skupině nikdo není') : t('people.emptyQuery')}
                    </p>
                  )}
                  {visibleMembers.map((person) => (
                    <div key={person.id} className="flex items-center gap-3 rounded-xl border border-white/10 px-3 py-2">
                      <button type="button" className="flex min-w-0 flex-1 items-center gap-3 text-left" onClick={() => openPerson(person)}>
                        <EntityAvatar label={person.display_name || person.login} size={32} />
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-medium">{person.display_name}</span>
                          <span className="block truncate font-mono text-xs text-muted-foreground">{person.login}</span>
                        </span>
                      </button>
                      {canWrite && (
                        <Button
                          variant="ghost"
                          size="sm"
                          loading={bulkMut.isPending}
                          onClick={() => bulkMut.mutate({ action: 'unassign', group: openedGroup.name, ids: [person.id] })}
                        >{t('people.unassign')}</Button>
                      )}
                    </div>
                  ))}
                </div>
              </ProfileSection>
            </div>
          );
        })()}
      </Modal>

      <Modal
        open={Boolean(passwordFor)}
        onClose={closePassword}
        title={passwordDone.length > 0 ? t('people.passwordReady', 'Heslo je nastavené') : t('people.changePassword', 'Změnit heslo')}
        size="md"
      >
        {passwordFor && passwordDone.length === 0 && (() => {
          const bulk = passwordFor.id === '__bulk__';
          const manualBad = passMode === 'manual' &&
            passwordHints(customPassword, {
              ...(bulk ? {} : {
                login: passwordFor.login,
                names: [passwordFor.display_name],
                emails: [passwordFor.email],
              }),
              minLength: policyMin,
              requireComplexity: true,
            }).length > 0;
          return (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {passwordFor.display_name}{passwordFor.login ? <> · <span className="font-mono">{passwordFor.login}</span></> : null}
              </p>
              <PasswordChooser
                mode={passMode}
                onMode={setPassMode}
                value={customPassword}
                onValue={setCustomPassword}
                words={words}
                onWords={setWords}
                complexity={complexity}
                onComplexity={setComplexity}
                context={bulk ? undefined : {
                  login: passwordFor.login,
                  names: [passwordFor.display_name],
                  emails: [passwordFor.email],
                }}
                minLength={policyMin}
                minLengthSource={policySource}
                fieldMode="change"
                check={checkTool}
                showUnique={bulk}
                uniqueEach={uniqueEach}
                onUniqueEach={setUniqueEach}
                showTemporary={active?.kind !== 'entity'}
                temporary={temporary}
                onTemporary={setTemporary}
                rejected={rejected}
              />
              <RowErrors rows={rowErrors} />
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={closePassword}>{t('people.close')}</Button>
                <Button
                  variant="primary"
                  icon={KeyRound}
                  loading={passwordMut.isPending || bulkMut.isPending}
                  disabled={!customPassword || manualBad}
                  onClick={() => {
                    if (bulk) {
                      const passwords: Record<string, string> = {};
                      for (const person of selectedPeople) {
                        passwords[person.id] = uniqueEach && passMode !== 'manual'
                          ? makePassword(passMode, { words, complexity })
                          : customPassword;
                      }
                      bulkMut.mutate({ action: 'password', passwords, temporary });
                      return;
                    }
                    passwordMut.mutate({ person: passwordFor, password: customPassword, temporary });
                  }}
                >{t('people.savePassword')}</Button>
              </div>
            </div>
          );
        })()}
        {passwordFor && passwordDone.length > 0 && (
          <div className="space-y-4">
            <RowErrors rows={rowErrors} />
            {passwordDone.length === 1 ? (
              <div className="space-y-2 text-center">
                <p className="text-sm text-muted-foreground">
                  {passwordDone[0]!.name} · <span className="font-mono">{passwordDone[0]!.login}</span>
                </p>
                <code className="block select-all rounded-lg border border-white/10 bg-white/[0.03] px-4 py-4 font-mono text-2xl tracking-wide">
                  {passwordDone[0]!.password}
                </code>
              </div>
            ) : (
              <div className="max-h-80 overflow-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-2 py-2 font-medium">{t('people.name')}</th>
                      <th className="px-2 py-2 font-medium">{t('people.login')}</th>
                      <th className="px-2 py-2 font-medium">{t('people.password')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {passwordDone.map((row) => (
                      <tr key={row.login} className="border-t border-white/10">
                        <td className="px-2 py-2">{row.name}</td>
                        <td className="px-2 py-2 font-mono">{row.login}</td>
                        <td className="px-2 py-2 font-mono select-all">{row.password}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              {temporary && active?.kind !== 'entity'
                ? t('people.mustChangeHint', 'Při prvním přihlášení si zvolí vlastní heslo, které mu pak už platí.')
                : t('people.passwordOnce')}
            </p>
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="secondary"
                icon={Copy}
                onClick={() => {
                  const text = passwordDone.length === 1
                    ? passwordDone[0]!.password
                    : passwordDone.map((row) => `${row.login}\t${row.password}`).join('\n');
                  void navigator.clipboard.writeText(text).then(() => toast.success(t('people.copied')));
                }}
              >{t('people.copy')}</Button>
              {passwordDone.length > 1 && (
                <Button
                  variant="secondary"
                  onClick={() => downloadTable(passwordDone, [t('people.login'), t('people.name'), t('people.password')])}
                >{t('people.exportTable', 'Stáhnout tabulku')}</Button>
              )}
              <Button
                variant="secondary"
                icon={Printer}
                onClick={() => {
                  const ok = printSlips(passwordDone, {
                    title: t('people.slipTitle', 'Přihlašovací údaje'),
                    login: t('people.login'),
                    password: t('people.password'),
                    note: temporary && active?.kind !== 'entity'
                      ? t('people.slipNote', 'Při prvním přihlášení si heslo změníš na vlastní.')
                      : '',
                  });
                  if (!ok) toast.error(t('people.printBlocked', 'Prohlížeč zablokoval okno pro tisk'));
                }}
              >{t('people.printSlips', 'Vytisknout lísteček')}</Button>
              <Button variant="primary" onClick={closePassword}>{t('people.done', 'Hotovo')}</Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={assignOpen} onClose={() => setAssignOpen(false)} title={t('people.assign')} size="md">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{t('people.assignHint')}</p>
          <div>
            <Label>{t('people.group')}</Label>
            {directoryGroups.length > 0 ? (
              <Select
                value={groupName}
                onChange={(event) => setGroupName(event.target.value)}
                searchable
                searchPlaceholder={t('people.search')}
                options={[
                  { value: '', label: '—' },
                  ...directoryGroups.map((group) => ({ value: group.name, label: group.name })),
                ]}
              />
            ) : (
              <Input value={groupName} onChange={(event) => setGroupName(event.target.value)} />
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="md" className="h-10" onClick={() => setAssignOpen(false)}>{t('people.close')}</Button>
            <Button
              variant="primary"
              size="md"
              className="h-10"
              loading={bulkMut.isPending}
              disabled={!groupName.trim()}
              onClick={() => bulkMut.mutate({
                action: 'assign',
                group: groupName.trim(),
                ids: assignIds ?? undefined,
              })}
            >{t('people.assign')}</Button>
          </div>
        </div>
      </Modal>

      <Modal open={Boolean(openedStation)} onClose={() => setOpenedStation(null)} title={openedStation?.name || t('people.paneStations', 'Stanice')} size="md">
        {openedStation && (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div className="grid flex-1 gap-3 sm:grid-cols-2">
                <Fact label={t('people.stationDns', 'DNS')}>{openedStation.dns || '—'}</Fact>
                <Fact label={t('people.stationOs', 'Systém')}>{openedStation.os || '—'}</Fact>
                <Fact label={t('people.place')}>{openedStation.place || '—'}</Fact>
                <Fact label={t('people.stationKind', 'Druh')}>{t(`people.machine.${openedStation.kind}`, openedStation.kind)}</Fact>
                <Fact label={t('people.signedIn', 'Přihlášený')}>
                  <SignedIn sessions={sessionsFor(openedStation.name, openedStation.dns)} />
                </Fact>
                <Fact label="RustDesk"><DeskLink id={deskFor(openedStation.name, openedStation.dns)} /></Fact>
                <Fact label={t('people.lastLogon')}>{formatDay(openedStation.last_logon_at) || '—'}</Fact>
                <Fact label={t('people.description')}>{openedStation.description || '—'}</Fact>
              </div>
              {openedStation.origin !== 'fleet' && canWrite && (
                <Toggle
                  value={openedStation.enabled !== false}
                  label={openedStation.enabled === false ? t('people.off') : t('people.on')}
                  saving={stationMut.isPending}
                  onSave={async (enabled) => {
                    await stationMut.mutateAsync({ station: openedStation, enabled });
                  }}
                />
              )}
            </div>
          </div>
        )}
      </Modal>

      <Modal open={creating} onClose={() => setCreating(false)} title={t('people.create')} size="lg">
        <div className="space-y-4">
          <div className="flex gap-1 border-b border-white/10">
            <button
              type="button"
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${createMode === 'one' ? 'border-plex text-plex' : 'border-transparent text-muted-foreground'}`}
              onClick={() => setCreateMode('one')}
            >{t('people.oneAccount', 'Jeden účet')}</button>
            <button
              type="button"
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${createMode === 'table' ? 'border-plex text-plex' : 'border-transparent text-muted-foreground'}`}
              onClick={() => setCreateMode('table')}
            >{t('people.fromTable', 'Z tabulky')}</button>
          </div>
          {createMode === 'one' ? (
            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <Label>{t('people.firstName')}</Label>
                  <Input value={form.first} onChange={(event) => setForm({ ...form, first: event.target.value })} />
                </div>
                <div>
                  <Label>{t('people.lastName')}</Label>
                  <Input value={form.last} onChange={(event) => setForm({ ...form, last: event.target.value })} />
                </div>
              </div>
              <div>
                <Label>{t('people.login')}</Label>
                <Input value={form.login} onChange={(event) => setForm({ ...form, login: event.target.value })} placeholder="jan.novak" />
              </div>
              <div>
                <Label>{t('people.email')}</Label>
                <Input value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} />
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">{t('people.importHint', 'CSV, středník z Excelu, tabulátor, nebo jeden login na řádek. Sloupce: login, jméno, příjmení, e-mail, heslo, skupina. Vyplněné heslo zůstane, prázdné se vygeneruje.')}</p>
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" onClick={() => downloadText('accounts-template.csv', IMPORT_TEMPLATE)}>
                  {t('people.importTemplate', 'Stáhnout vzor')}
                </Button>
                <Button variant="secondary" onClick={() => fileRef.current?.click()}>
                  {t('people.importFile', 'Nahrát soubor')}
                </Button>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,.tsv,.txt,text/csv,text/plain"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = () => setImportText(String(reader.result ?? ''));
                    reader.readAsText(file);
                  }}
                />
              </div>
              <Textarea
                value={importText}
                placeholder={t('people.importPaste', 'Vložit řádky')}
                onChange={(event) => setImportText(event.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {imported.limited
                  ? t('people.importLimit', { count: MAX_IMPORT })
                  : imported.accounts.length > 0
                    ? t('people.importCount', { count: imported.accounts.length })
                    : t('people.importEmpty', 'Tabulka je prázdná')}
              </p>
              {imported.issues.slice(0, 4).map((issue) => (
                <p key={`${issue.code}-${issue.row}`} className="text-xs text-muted-foreground">
                  {issue.code === 'login'
                    ? t('people.importBad', { row: issue.row })
                    : t('people.importDup', { login: issue.login ?? '' })}
                </p>
              ))}
              {imported.accounts.slice(0, 6).map((row) => (
                <p key={row.login} className="truncate font-mono text-xs text-muted-foreground">
                  {row.login}{[row.first, row.last].filter(Boolean).length > 0 ? ` · ${[row.first, row.last].filter(Boolean).join(' ')}` : ''}
                </p>
              ))}
            </div>
          )}
          <PasswordChooser
            mode={passMode}
            onMode={setPassMode}
            value={customPassword}
            onValue={setCustomPassword}
            words={words}
            onWords={setWords}
            complexity={complexity}
            onComplexity={setComplexity}
            context={createMode === 'one'
              ? { login: form.login, names: [form.first, form.last], emails: [form.email] }
              : undefined}
            minLength={policyMin}
            minLengthSource={policySource}
            fieldMode="create"
            check={checkTool}
            showUnique={createMode === 'table'}
            uniqueEach={uniqueEach}
            onUniqueEach={setUniqueEach}
            showTemporary={active?.kind !== 'entity'}
            temporary={temporary}
            onTemporary={setTemporary}
            rejected={rejected}
          />
          <RowErrors rows={rowErrors} />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setCreating(false)}>{t('people.close')}</Button>
            <Button
              variant="primary"
              icon={UserPlus}
              loading={createMut.isPending}
              disabled={createMode === 'table' && (imported.accounts.length === 0 || imported.limited)}
              onClick={() => {
                if (createMode === 'one') {
                  if (!form.login.trim() || !form.first.trim() || !form.last.trim()) {
                    toast.error(t('people.missingNames'));
                    return;
                  }
                  createMut.mutate({
                    mode: 'one',
                    rows: withPasswords([{
                      login: form.login.trim(),
                      first: form.first.trim(),
                      last: form.last.trim(),
                      email: form.email.trim(),
                      password: '',
                      group: '',
                    }]),
                  });
                  return;
                }
                if (imported.limited || imported.accounts.length === 0) return;
                createMut.mutate({ mode: 'table', rows: withPasswords(imported.accounts) });
              }}
            >{createMode === 'table' ? t('people.createMany', { count: imported.accounts.length }) : t('people.create')}</Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={Boolean(consent)}
        title={t('people.unverifiedTitle')}
        description={t('people.unverifiedDescription', { count: consent?.rows.length ?? 0 })}
        confirmLabel={t('people.unverifiedConfirm')}
        onConfirm={() => {
          const pending = consent;
          setConsent(null);
          if (pending) createMut.mutate({ rows: pending.rows, mode: pending.mode, accept: true });
        }}
        onCancel={() => setConsent(null)}
      />
    </div>
  );
}
